import React from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Inbox, CheckCircle2, IndianRupee, CreditCard, Users, ArrowRight } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, AreaChart, Area, CartesianGrid, Cell } from "recharts";
import { adminApi } from "@/lib/api";
import { fmtMoney, timeAgo } from "@/lib/format";
import { PageHeader } from "@/components/common";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";

const STATUS_COLORS = { Pending: "#d97706", "Under Review": "#0284c7", Approved: "#059669", Rejected: "#e11d48", Failed: "#71717a", Refunded: "#7c3aed" };

const Kpi = ({ Icon, label, value, sub, testId, tone = "text-primary bg-teal-50" }) => (
  <Card className="card-hover"><CardContent className="p-5">
    <div className="flex items-center justify-between"><p className="text-sm text-muted-foreground">{label}</p><span className={`flex h-8 w-8 items-center justify-center rounded-lg ${tone}`}><Icon className="h-4 w-4" /></span></div>
    <p className="num mt-3 text-2xl font-semibold" data-testid={testId}>{value}</p>
    {sub && <p className="mt-1 text-xs text-muted-foreground">{sub}</p>}
  </CardContent></Card>
);

export default function AdminOverview() {
  const { data, isLoading } = useQuery({ queryKey: ["admin-stats"], queryFn: () => adminApi.get("/admin/stats").then((r) => r.data), refetchInterval: 20000 });
  if (isLoading || !data) return <div className="grid gap-4 md:grid-cols-4">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-28" />)}</div>;
  const statusData = Object.entries(data.by_status).map(([k, v]) => ({ status: k, count: v }));
  const at = data.approved_today || {};
  return (
    <div className="space-y-6">
      <PageHeader title="Overview" subtitle="Live payment operations at a glance." actions={<Button asChild className="gap-2" data-testid="overview-review-queue-button"><Link to="/admin/deposits?status=Pending">Review queue <ArrowRight className="h-4 w-4" /></Link></Button>} />
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
        <Kpi Icon={Inbox} label="Open requests" value={data.open_requests} sub={`${data.by_status.Pending} pending · ${data.by_status["Under Review"]} in review`} testId="kpi-open-requests" tone="bg-amber-50 text-amber-700" />
        <Kpi Icon={CheckCircle2} label="Approved today" value={(at.INR?.count || 0) + (at.USDT?.count || 0)} sub={`${fmtMoney(at.INR?.volume || 0, "INR")} · ${fmtMoney(at.USDT?.volume || 0, "USDT")}`} testId="kpi-approved-today" tone="bg-emerald-50 text-emerald-700" />
        <Kpi Icon={IndianRupee} label="Total INR credited" value={fmtMoney(data.volume?.INR?.volume || 0, "INR")} sub={`Bonus paid ${fmtMoney(data.volume?.INR?.bonus || 0, "INR")}`} testId="kpi-inr-volume" />
        <Kpi Icon={CreditCard} label="Active methods" value={`${data.active_methods}/${data.total_methods}`} testId="kpi-active-methods" tone="bg-sky-50 text-sky-700" />
        <Kpi Icon={Users} label="Users" value={data.users} testId="kpi-users" tone="bg-zinc-100 text-zinc-700" />
      </div>
      <div className="grid gap-4 lg:grid-cols-12">
        <Card className="lg:col-span-7">
          <CardHeader className="pb-2"><CardTitle className="text-base">Deposit requests · last 14 days (INR)</CardTitle></CardHeader>
          <CardContent className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data.trend} margin={{ left: 0, right: 8 }}>
                <defs><linearGradient id="gInr" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#0f766e" stopOpacity={0.25} /><stop offset="100%" stopColor="#0f766e" stopOpacity={0} /></linearGradient></defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="date" tickFormatter={(d) => d.slice(5)} fontSize={11} />
                <YAxis fontSize={11} width={56} />
                <Tooltip formatter={(v) => fmtMoney(v, "INR")} />
                <Area type="monotone" dataKey="INR" stroke="#0f766e" fill="url(#gInr)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
        <Card className="lg:col-span-5">
          <CardHeader className="pb-2"><CardTitle className="text-base">Requests by status</CardTitle></CardHeader>
          <CardContent className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={statusData} layout="vertical" margin={{ left: 20 }}>
                <XAxis type="number" allowDecimals={false} fontSize={11} />
                <YAxis type="category" dataKey="status" fontSize={11} width={84} />
                <Tooltip />
                <Bar dataKey="count" radius={[0, 4, 4, 0]}>{statusData.map((s) => <Cell key={s.status} fill={STATUS_COLORS[s.status]} />)}</Bar>
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-2"><CardTitle className="text-base">Recent admin activity</CardTitle><Button asChild variant="ghost" size="sm" data-testid="overview-view-audit"><Link to="/admin/audit-logs">View audit log</Link></Button></CardHeader>
        <CardContent>
          <ul className="divide-y">
            {data.recent_audit.map((a) => (
              <li key={a.id} className="flex items-center gap-3 py-2.5 text-sm">
                <span className="rounded bg-secondary px-2 py-0.5 font-mono text-xs">{a.action}</span>
                <span className="truncate text-muted-foreground">{a.entity_id}</span>
                <span className="ml-auto whitespace-nowrap text-xs text-muted-foreground">{a.actor_email} · {timeAgo(a.created_at)}</span>
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>
    </div>
  );
}
